import { useEffect, useState } from "react";

/**
 * Where a system button appears in the settings panel.
 *
 * The pipeline and admin actions — «Папка готова — в конвейер», «Во все языки», Check Data —
 * are written where their logic lives: in the toolbars, next to the state they act on. They are
 * SHOWN in the settings panel, portalled into a slot the panel provides, so each button keeps
 * its own loading flags, confirmations and handlers and only its position changes.
 *
 * The first version of this had ONE slot: every button portalled into the same bucket in
 * whatever order the components happened to mount. That cannot express categories — the panel
 * had no way to file «Папка готова» under «Сдача» and Check Data under «Служебное», nor to say
 * in words what either does. So a slot is now addressed by id and the panel owns the catalogue
 * (Footer/settingsActions.ts): which group an id belongs to, what it is called, what pressing it
 * does. A button no longer decides where it lands or how it is labelled; it only says
 * "I am here", and that is the second half of this module:
 *
 *   • CLAIM — a button announces its id while it applies. Half of these actions are
 *     conditional (a stream action outside a stream, a handover on the master side), and a row
 *     whose button never comes is a heading and an explanation over an empty space — worse than
 *     not listing it, because the reader goes hunting for a control that was never there. The
 *     condition is known only to the button, so the button declares it.
 *   • SLOT — the panel hands back the element to portal into, or null while the panel has never
 *     been opened, in which case nothing renders and a closed panel costs nothing.
 */

const slots = new Map<string, HTMLElement>();
const slotListeners = new Map<string, Set<(el: HTMLElement | null) => void>>();

const claims = new Set<string>();
const claimListeners = new Set<(ids: Set<string>) => void>();

/** Called by the panel as it mounts and unmounts the container for one action. */
export const setSettingsSlot = (id: string, el: HTMLElement | null): void => {
    if ((slots.get(id) ?? null) === el) return;
    if (el) slots.set(id, el);
    else slots.delete(id);
    slotListeners.get(id)?.forEach((fn) => fn(el));
};

/**
 * Claim a place in the settings panel and get the element to portal into.
 *
 * Pass active=false when the action does not apply right now: its row then disappears from the
 * panel instead of standing empty. Returns null while the panel has never been opened.
 */
export const useSettingsSlot = (id: string, active = true): HTMLElement | null => {
    const [element, setElement] = useState<HTMLElement | null>(() => slots.get(id) ?? null);

    useEffect(() => {
        if (!active) return;
        claims.add(id);
        const snapshot = new Set(claims);
        claimListeners.forEach((fn) => fn(snapshot));
        return () => {
            claims.delete(id);
            const left = new Set(claims);
            claimListeners.forEach((fn) => fn(left));
        };
    }, [id, active]);

    useEffect(() => {
        const forId = slotListeners.get(id) ?? new Set<(el: HTMLElement | null) => void>();
        forId.add(setElement);
        slotListeners.set(id, forId);
        setElement(slots.get(id) ?? null);
        return () => {
            forId.delete(setElement);
            if (forId.size === 0) slotListeners.delete(id);
        };
    }, [id]);

    return active ? element : null;
};

/** Which actions exist on screen right now — the panel shows a row only for these. */
export const useSettingsClaims = (): Set<string> => {
    const [current, setCurrent] = useState<Set<string>>(() => new Set(claims));
    useEffect(() => {
        claimListeners.add(setCurrent);
        setCurrent(new Set(claims));
        return () => {
            claimListeners.delete(setCurrent);
        };
    }, []);
    return current;
};
